import { addRemoveFavouritesApi } from '@api/useQueryCalls';
import AddProductTile from '@atoms/AddProductTile/index';
import RemoteImage from '@atoms/RemoteImage';
import { Text } from '@components/primitives';
import { useToast } from '@hooks';
import { logFavouriteAction } from '@molecules/TopProducts/logUtils';
import appStyles from '@root/appStyles';
import { useQueryClient } from '@tanstack/react-query';
import { AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { showSuccessToast } from '@utils/toastManager';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View, TouchableOpacity } from 'react-native';
import styles from './styles';

interface AddFavTileInt {
  id: number;
  name: string;
  iconLink?: string;
  icon: string;
  screenName: string;
  availability: boolean;
  canShowNew?: boolean;
}

interface AddFavTileProps {
  bpCategoryProducts: AddFavTileInt;
  onPressHandler: () => void;
  onAddFavSuccess: () => void;
}

function AddFavTile({ bpCategoryProducts, onPressHandler, onAddFavSuccess }: AddFavTileProps) {
  const toast = useToast();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const onPressToast = () => {
    showSuccessToast(toast, t('favourites.successAddFavorite', { name: bpCategoryProducts?.name }), '2', 'top', 2500);
  };

  const { mutate: isFavouriteApi } = addRemoveFavouritesApi({
    payload: {
      categoryId: bpCategoryProducts.id,
      favorite: true,
    },
    onSuccess: () => {
      onAddFavSuccess(bpCategoryProducts.id);
      queryClient.invalidateQueries({ queryKey: ['billpayment_api'] });
      onPressToast();
    },
  });

  const handlePress = useCallback(() => {
    logFavouriteAction(true, AMPLITUDE_EVENT_SOURCES.home_page, bpCategoryProducts.id);
    isFavouriteApi();
    onPressHandler();
  }, []);

  const dynamicStyles = useMemo(
    () => ({
      tileButton: [
        appStyles.borderWithShadow,
        styles.productTileButton,
        styles.enabledDisabledStyles(bpCategoryProducts),
      ],
      opacityStyle: styles.enabledDisabledOpacity(bpCategoryProducts),
    }),
    [bpCategoryProducts],
  );

  return (
    <View style={styles.productTile}>
      <AddProductTile onAdd={handlePress} />
      <TouchableOpacity
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={`bottomsheet_${bpCategoryProducts.screenName}`}
        testID={`btn${bpCategoryProducts.id}`}
        onPress={handlePress}
        style={dynamicStyles.tileButton}>
        <View key={bpCategoryProducts.name} style={dynamicStyles.opacityStyle}>
          <RemoteImage width={32} height={32} uri={bpCategoryProducts.iconLink || bpCategoryProducts.icon} />
        </View>
      </TouchableOpacity>
      <Text testID={bpCategoryProducts.screenName} style={styles.productNameText} variant="sm-normal">
        {bpCategoryProducts.screenName}
      </Text>
    </View>
  );
}

export default React.memo(AddFavTile);
